import type { AudioRecorderContainerRef } from "@features/session/containers/AudioRecorderContainer";
import { isWeb } from "@helpers/platform";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { useToast } from "@memorang/ui/src/components/Toast";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useSessionStore from "../../../hooks/useSessionStore";
import ItemWrapper from "../../ItemWrapper";
import Speaking from "./variants/Speaking";

type Props = {
	item: BaseItemSchema;
	response: Extract<ItemResponse, { type: "recording" }>;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
};

const SpeakingContainer = ({
	item,
	response,
	toggleShowReadMore,
	originalItemType,
}: Props) => {
	const { showToast } = useToast();
	const { t } = useTranslation();
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse =
		item.answer?.aiResponse?.answer || userResponse?.[item.id!];

	const itemStates = useSessionStore((state) => state.itemStates);
	const itemState = itemStates?.get(item.id!);
	const isAnswered = itemState?.answered || item.answer != null;

	const { isMobile } = useDevice();

	const audioRecorderContainerRef = useRef<AudioRecorderContainerRef>(null);

	return (
		<ItemWrapper
			item={item}
			response={response}
			instructions={item.content?.instruction?.text}
			toggleShowReadMore={() => {
				if (audioRecorderContainerRef.current?.isRecording) {
					audioRecorderContainerRef.current?.stop();
					showToast({
						message: t("recorder.uiElementPressed"),
					});
				}
				toggleShowReadMore?.();
			}}
			originalItemType={originalItemType}
			footer={
				!isWeb && (
					<Box padding={16} paddingBottom={24}>
						<Speaking
							itemId={item.id!}
							currentQuestionAnswered={isAnswered}
							currentUserResponse={currentUserResponse}
							maximumDuration={response.maxDuration}
							audioRecorderContainerRef={audioRecorderContainerRef}
						/>
					</Box>
				)
			}
		>
			{isWeb && (
				<Box paddingHorizontal={isMobile ? 16 : 0} paddingBottom={24}>
					<Speaking
						itemId={item.id!}
						currentQuestionAnswered={isAnswered}
						currentUserResponse={currentUserResponse}
						maximumDuration={response.maxDuration}
						audioRecorderContainerRef={audioRecorderContainerRef}
					/>
				</Box>
			)}
		</ItemWrapper>
	);
};

export default SpeakingContainer;
